import { mkConfig, generateCsv, download } from 'export-to-csv';
import { isString } from 'lodash-es';
import { DateTime } from 'luxon';
import { formatNumber, formatPercent } from '../../../helpers/format-number';
import { formatIdNumber } from '@startuptools/common/id-number';
import { DATE_FORMAT_LUXON } from '@startuptools/common/common';
import { type Row } from '../reports/ShareholderOverviewTable';
import { type GqlCompleteShareCapitalChange } from '../../../graphql/base-types.graphql';

export const downloadSccsCsv = (
  language: string,
  rows: GqlCompleteShareCapitalChange[],
  companyName: string,
  hasFoundation: boolean,
) => {
  const formatDate = (date: DateTime | null | undefined) => date?.toLocaleString(DateTime.DATE_SHORT) ?? null;
  const swedish = language === 'sv';

  const csvData = rows.map(row => {
    const baseFields1 = {
      [swedish ? 'Beslutat' : 'Decision Date']: row.decisionDate ? formatDate(DateTime.fromISO(row.decisionDate)) : '',
      [swedish ? 'Registrerat' : 'Registered']: row.registrationDate
        ? formatDate(DateTime.fromISO(row.registrationDate))
        : '',
      [swedish ? 'Ändring' : 'Change']: row.name,
      [swedish ? 'Förändrat antal aktier' : 'Changed Number Of Shares']: row.changedNumberOfShares,
    };

    const baseFields2 = {
      [swedish ? 'Aktiekapital före' : 'Share Capital Before']: row.shareCapitalBefore,
      [swedish ? 'Förändrat aktiekapital' : 'Share Capital Changed']: row.shareCapitalChanged,
      [swedish ? 'Aktiekapital efter' : 'Share Capital After']: row.shareCapitalAfter,
      [swedish ? 'Kvotvärde' : 'Share Price Factor']: row.sharePriceFactor,
      [swedish ? 'Diarienummer' : 'Case Number']: row.caseNumber,
    };

    if (hasFoundation) {
      return {
        ...baseFields1,
        [swedish ? 'Antal aktier efter transaktion' : 'Number of Shares After Transaction']: row.numberOfShares,
        [swedish ? 'Pris per aktie (genomsnitt)' : 'Price Per Share (average)']: row.pricePerShare,
        [swedish ? 'Förändrat antal aktier (splitjusterat)' : 'Changed Number Of Shares (Split Adjusted)']:
          row.changedNumberOfSharesSplitAdjusted,
        [swedish
          ? 'Antal aktier efter transaktion (splitjusterat)'
          : 'Number Of Shares after Transaction (Split Adjusted)']: row.numberOfSharesSplitAdjusted,
        [swedish ? 'Pris per aktie (genomsnitt) (splitjusterat)' : 'Price Per Share (Split Adjusted)']:
          row.pricePerShareSplitAdjusted,
        [swedish ? 'Pre-money' : 'Pre-Money']: row.preMoney,
        [swedish ? 'Betalt' : 'Paid Amount']: row.paidAmount,
        [swedish ? 'Post-money' : 'Post-Money']: row.postMoney,
        ...baseFields2,
      };
    } else {
      return { ...baseFields1, ...baseFields2 };
    }
  });

  const csvConfig = mkConfig({
    filename:
      language === 'sv'
        ? `Aktiekapitalförändringar_${companyName}_${language}`
        : `Share_Capital_Changes_${companyName}_${language}`,
    useKeysAsHeaders: true,
  });

  const csv = generateCsv(csvConfig)(csvData);
  download(csvConfig)(csv);
};

export const downloadShareholderOverviewCsv = (companyName: string, states: Row[], date?: DateTime | null) => {
  const csvData = states.map(row => {
    if (!row.identity) {
      throw new Error('missing identity');
    }
    return {
      Namn: row.identity.name,
      'Person/Org-nummer': row.identity.isSwedish ? formatIdNumber(row.identity.identityNumber) : '',
      Epost: row.identity.email || '',
      Adress: isString(row.identity.address) ? row.identity.address.replace(/\n/g, ' ').trim() : '',
      Telefon: row.identity.phone || '',
      Aktieslag: row.shareClass,
      'Antal aktier': formatNumber(row.numberOfShares, 0),
      Andel: formatPercent(row.percent, 2),
    };
  });
  const csvConfig = mkConfig({
    filename: `Aktieägaröversikt_${companyName}${date ? '_' + date.toFormat(DATE_FORMAT_LUXON) : ''}`,
    useKeysAsHeaders: true,
    useBom: true,
  });
  const csv = generateCsv(csvConfig)(csvData);
  download(csvConfig)(csv);
};
